import { socketIOSettings } from '@config/socket.io';
import { API_ENDPOINT } from '@constants/env';
import {
  PROVISIONING_EVENTS,
  type TProvisioningDoneEvent,
  type TProvisioningFailedEvent,
  type TProvisioningStepEvent,
} from '@conversed/shared/src/central/constants/socket';
import {
  reconnectAfterRetryHint,
  reconnectAfterServerDisconnect,
} from '@helpers/socketHelpers';
import { useMainSelector } from '@hooks/useMainSelector';
import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';

const { STEP, DONE, FAILED } = PROVISIONING_EVENTS.BACKEND_TO_PLATFORM;

// * Listen-only: the pipeline is triggered over HTTP (create / retry), not a
// * socket emit, so this hook only subscribes to the backend's progress events.
// * Only the initiating session receives them (emitToUser), so the status page
// * also polls the DB for cross-session viewers (see useProvisioningStatus).
export const useProvisioningSocket = (
  onStep?: (data: TProvisioningStepEvent) => void,
  onDone?: (data: TProvisioningDoneEvent) => void,
  onFailed?: (data: TProvisioningFailedEvent) => void,
) => {
  const {
    login: { accessToken },
  } = useMainSelector();

  const socketRef = useRef<Socket | null>(null);

  // * Store callbacks in refs so the socket is not reconnected when they change.
  const onStepRef = useRef(onStep);
  const onDoneRef = useRef(onDone);
  const onFailedRef = useRef(onFailed);

  useEffect(() => {
    onStepRef.current = onStep;
  }, [onStep]);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    onFailedRef.current = onFailed;
  }, [onFailed]);

  useEffect(() => {
    if (!accessToken) {
      return;
    }

    if (socketRef.current?.connected) {
      return;
    }

    socketRef.current = io(API_ENDPOINT, {
      ...socketIOSettings,
      path: '/sockets/platform',
      auth: {
        token: accessToken,
      },
    });
    reconnectAfterServerDisconnect(socketRef.current);
    const cancelRetryReconnect = reconnectAfterRetryHint(socketRef.current);

    socketRef.current.on(STEP, (data: TProvisioningStepEvent) => {
      onStepRef.current?.(data);
    });

    socketRef.current.on(DONE, (data: TProvisioningDoneEvent) => {
      onDoneRef.current?.(data);
    });

    socketRef.current.on(FAILED, (data: TProvisioningFailedEvent) => {
      onFailedRef.current?.(data);
    });

    return () => {
      cancelRetryReconnect();

      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [accessToken]);
};
